import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/v5/components/ui/alert-dialog";
import { Checkbox } from "@/v5/components/ui/checkbox";
import { useVideoCallContext } from "@/v5/contexts/VideoCallContext";
import {
  defaultVideoInviteChannels,
  VIDEO_INVITE_CHANNEL_LABEL,
  VIDEO_INVITE_CHANNELS,
  videoInviteReasonText,
  type VideoInviteChannel,
} from "@/v5/lib/videoInvite";
import { useEffect, useState } from "react";

export function VideoCallConfirmDialog() {
  const {
    pendingConfirm,
    inviteOptions,
    inviteOptionsLoading,
    confirmVideoCall,
    cancelVideoCall,
  } = useVideoCallContext();

  const [channels, setChannels] = useState<VideoInviteChannel[]>([]);

  const open = pendingConfirm != null;
  const patientName = pendingConfirm?.patientName || "this patient";

  // Seed the ticks once we know what this patient has on file. Text only —
  // pressing Start call without reading the dialog does what it always did.
  useEffect(() => {
    if (!open || inviteOptionsLoading) return;
    setChannels(defaultVideoInviteChannels(inviteOptions));
  }, [open, inviteOptionsLoading, inviteOptions]);

  // With no options loaded yet we cannot say a channel is unusable, so it stays
  // offered and the send reports the truth.
  const isAvailable = (channel: VideoInviteChannel) =>
    inviteOptions?.[channel]?.available ?? true;
  const anyAvailable = VIDEO_INVITE_CHANNELS.some(isAvailable);

  const toggle = (channel: VideoInviteChannel, checked: boolean) => {
    setChannels((current) =>
      checked
        ? current.includes(channel)
          ? current
          : [...current, channel]
        : current.filter((c) => c !== channel),
    );
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) cancelVideoCall();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Start video call?</AlertDialogTitle>
          <AlertDialogDescription>
            {anyAvailable
              ? `Start a video call with ${patientName} and send them a link to join by:`
              : `Start a video call with ${patientName}? There's no way to text or email them the link, so you'll need to copy it from the call screen and send it yourself.`}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {anyAvailable && (
          <div className="space-y-3">
            {VIDEO_INVITE_CHANNELS.map((channel) => {
              const state = inviteOptions?.[channel] ?? null;
              const available = isAvailable(channel);
              const disabled = inviteOptionsLoading || !available;
              const inputId = `video-invite-${channel}`;

              return (
                <div key={channel}>
                  <div className="flex items-center gap-2.5">
                    <Checkbox
                      id={inputId}
                      checked={channels.includes(channel)}
                      disabled={disabled}
                      onCheckedChange={(checked) => toggle(channel, checked === true)}
                    />
                    <label
                      htmlFor={inputId}
                      className={`text-sm ${disabled ? "text-muted-foreground" : "cursor-pointer"}`}
                    >
                      {VIDEO_INVITE_CHANNEL_LABEL[channel]}
                    </label>
                    {available && state?.destination && (
                      <span className="text-sm text-muted-foreground truncate">
                        {state.destination}
                      </span>
                    )}
                    {inviteOptionsLoading && (
                      <span className="text-sm text-muted-foreground">Checking…</span>
                    )}
                  </div>
                  {!available && !inviteOptionsLoading && (
                    <p className="mt-1 ml-6 text-xs text-muted-foreground">
                      {videoInviteReasonText(channel, state?.reason)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={cancelVideoCall}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => confirmVideoCall(channels)}>
            Start call
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
